/**
 * activity_catalog.ts — ACTIVITY STATIONS and the states they host (society
 * wave, 2026-09-19), pure.
 *
 * WHY: measured on the live world, the largest single versona state was
 * `chatting` (21–42 %): the mover turns any arrival with company into a
 * face-to-face talk pose, and since the ×5 population almost everyone has
 * company. The animation layer owns sitting and drinking clips it never plays,
 * because its law is NEVER MIME — no sitting without a seat, no carrying without
 * a prop. A STATION is the missing anchor: a bench, a tavern table, the drill
 * green, the well queue. The server seeds stations per settlement and sends a
 * versona there with an activity state; the CLIENT seats whoever is present in
 * the station's FORMATION (sorted by id, so every client agrees) and draws the
 * prop. Seating is presentation: the server keeps one authoritative position
 * per versona and never arbitrates seats.
 *
 * Venue gating follows `TIER_SLOTS` (hamlet 5 venues, town 8, city 12): a
 * station names the venue it prefers and a fallback that exists at every tier.
 *
 * MIRROR: client/src/lib/activity_catalog.ts must stay byte-identical.
 */

export type Formation = 'row' | 'table' | 'circle' | 'arc' | 'queue' | 'grid' | 'ring';

/** The seven town scores an activity may feed (lib/town_scores.ts). */
export type ScoreKey = 'cohesion' | 'readiness' | 'learning' | 'health' | 'order' | 'piety' | 'prosperity';

export interface StationDef {
  kind: string;
  /** Preferred venue slot key, then the fallback that exists at every tier. */
  venue: string;
  fallbackVenue: string;
  /** Offset from the venue centre, metres (keeps stations off the doorstep). */
  dx: number;
  dz: number;
  capacity: number;
  formation: Formation;
  /** Spacing between formation slots, metres. */
  spacing: number;
  /** Lowest tier that gets this station ('hamlet' = everyone). */
  minTier: 'hamlet' | 'town' | 'city';
  /** Procedural prop the client draws at the station ('' = open ground). */
  prop: string;
}

export const STATIONS: readonly StationDef[] = [
  { kind: 'bench', venue: 'plaza', fallbackVenue: 'plaza', dx: 7, dz: 3, capacity: 4, formation: 'row', spacing: 0.9, minTier: 'hamlet', prop: 'bench' },
  { kind: 'bench_b', venue: 'plaza', fallbackVenue: 'plaza', dx: -7, dz: 4, capacity: 4, formation: 'row', spacing: 0.9, minTier: 'town', prop: 'bench' },
  { kind: 'tavern_table', venue: 'tavern', fallbackVenue: 'plaza', dx: 5, dz: -4, capacity: 4, formation: 'table', spacing: 1.1, minTier: 'hamlet', prop: 'table' },
  { kind: 'tavern_table_b', venue: 'tavern', fallbackVenue: 'plaza', dx: -5, dz: -5, capacity: 4, formation: 'table', spacing: 1.1, minTier: 'town', prop: 'table' },
  { kind: 'story_circle', venue: 'tavern', fallbackVenue: 'plaza', dx: 0, dz: 8, capacity: 8, formation: 'arc', spacing: 1.2, minTier: 'hamlet', prop: 'hearth' },
  { kind: 'green', venue: 'plaza', fallbackVenue: 'plaza', dx: 0, dz: 16, capacity: 12, formation: 'grid', spacing: 2.2, minTier: 'hamlet', prop: '' },
  { kind: 'dance_floor', venue: 'stage', fallbackVenue: 'plaza', dx: 0, dz: -12, capacity: 10, formation: 'circle', spacing: 1.8, minTier: 'hamlet', prop: '' },
  { kind: 'ration_line', venue: 'granary', fallbackVenue: 'granary', dx: 4, dz: 5, capacity: 8, formation: 'queue', spacing: 0.9, minTier: 'hamlet', prop: 'ration_table' },
  { kind: 'market_browse', venue: 'market', fallbackVenue: 'market', dx: -4, dz: 5, capacity: 6, formation: 'row', spacing: 1.3, minTier: 'hamlet', prop: 'stall' },
  { kind: 'kneelers', venue: 'shrine', fallbackVenue: 'plaza', dx: 0, dz: 5, capacity: 6, formation: 'arc', spacing: 1.0, minTier: 'hamlet', prop: 'kneeler' },
  { kind: 'school_mat', venue: 'archive', fallbackVenue: 'plaza', dx: 9, dz: -7, capacity: 8, formation: 'arc', spacing: 1.0, minTier: 'hamlet', prop: 'mat' },
  { kind: 'well_queue', venue: 'well', fallbackVenue: 'plaza', dx: 3, dz: 2, capacity: 6, formation: 'queue', spacing: 0.9, minTier: 'town', prop: 'well_bucket' },
  { kind: 'butts', venue: 'guardpost', fallbackVenue: 'plaza', dx: 8, dz: 6, capacity: 4, formation: 'row', spacing: 1.6, minTier: 'town', prop: 'targets' },
  { kind: 'wrestling_ring', venue: 'guardpost', fallbackVenue: 'plaza', dx: -8, dz: 8, capacity: 8, formation: 'ring', spacing: 1.4, minTier: 'town', prop: 'ring_rope' },
  { kind: 'infirmary_cots', venue: 'guardpost', fallbackVenue: 'plaza', dx: -6, dz: -6, capacity: 4, formation: 'row', spacing: 1.8, minTier: 'town', prop: 'cot' },
  { kind: 'archive_desks', venue: 'archive', fallbackVenue: 'archive', dx: 4, dz: 4, capacity: 4, formation: 'row', spacing: 1.4, minTier: 'city', prop: 'desk' },
  { kind: 'bath_yard', venue: 'bathhouse', fallbackVenue: 'bathhouse', dx: 5, dz: 5, capacity: 6, formation: 'grid', spacing: 1.8, minTier: 'city', prop: '' },
  // ── 2026-09-24: THE FIRE CIRCLE and the two TOWN TERMINALS ──
  // A full ring of log benches round a campfire (Sky's chat benches, Valheim's rested fire): versonas gather at night,
  // a player sits on a free seat. The terminals are stations that host no activity (capacity 0): seeding them this
  // way puts them on CLEAR GROUND by the same nudge every station takes, and the server checks a player's presence
  // against the very row the client stands the model on (the bounty guild's terminal, the contract board).
  { kind: 'fire_circle', venue: 'tavern', fallbackVenue: 'plaza', dx: -10, dz: 10, capacity: 8, formation: 'circle', spacing: 1.6, minTier: 'hamlet', prop: 'fire_ring' },
  { kind: 'bounty_terminal', venue: 'guardpost', fallbackVenue: 'plaza', dx: 9, dz: -5, capacity: 0, formation: 'row', spacing: 1, minTier: 'town', prop: '' },
  { kind: 'contract_board', venue: 'market', fallbackVenue: 'plaza', dx: 7, dz: -7, capacity: 0, formation: 'row', spacing: 1, minTier: 'hamlet', prop: '' },
  // BANKING (2026-09-24): a branch of the Commons Reserve in every town, city and capital ward — the terminal where
  // cash goes in and out, loans are signed and pledges handed over, the safe deposit box and the auction block.
  { kind: 'bank_terminal', venue: 'market', fallbackVenue: 'plaza', dx: -8, dz: -8, capacity: 0, formation: 'row', spacing: 1, minTier: 'town', prop: '' },
];

/** The station kinds that are TOWN TERMINALS (no activity, no seats; the client stands a model there). */
export const TERMINAL_KINDS: ReadonlySet<string> = new Set(['bounty_terminal', 'contract_board', 'bank_terminal']);
/** How near a terminal a player must stand to use it (the server's check and the client's prompt). */
export const TERMINAL_USE_M = 8;

const TIER_RANK: Record<string, number> = { hamlet: 0, circuit: 1, town: 1, city: 2, district: 2, capital: 2 };
/** The stations a settlement of this tier is seeded with. */
export function stationsForTier(tier: string): StationDef[] {
  const rank = TIER_RANK[tier] ?? 0;
  return STATIONS.filter((s) => (TIER_RANK[s.minTier] ?? 0) <= rank);
}
const STATION_BY_KIND: Record<string, StationDef> = Object.fromEntries(STATIONS.map((s) => [s.kind, s]));
export function stationDef(kind: string): StationDef | null {
  return STATION_BY_KIND[kind] ?? null;
}

export interface ActivityDef {
  /** The value `versona_goal.state` / `versona_state.state` carries. */
  state: string;
  /** Station kinds that host it ('' = at home / on a route, no station). */
  stations: string[];
  /** Pose family the client uses for the far / middle tier and as the clip key. */
  pose: 'seated' | 'seated_floor' | 'standing' | 'kneeling' | 'lying' | 'exertion' | 'stoop' | 'walk';
  /** Hand prop the client attaches (never-mime: the prop is what makes the clip honest). */
  prop: string;
  score: ScoreKey | '';
  /** Need the activity fills for the versona on completion. */
  need: 'social' | 'rest' | 'none';
  /** Who may do it. */
  who: 'adult' | 'child' | 'elder' | 'any';
}

export const ACTIVITIES: readonly ActivityDef[] = [
  { state: 'sitting', stations: ['bench', 'bench_b'], pose: 'seated', prop: '', score: '', need: 'rest', who: 'any' },
  { state: 'dozing', stations: ['bench', 'bench_b'], pose: 'seated', prop: '', score: '', need: 'rest', who: 'elder' },
  { state: 'drinking_seated', stations: ['tavern_table', 'tavern_table_b'], pose: 'seated', prop: 'mug', score: 'cohesion', need: 'social', who: 'adult' },
  { state: 'dicing', stations: ['tavern_table', 'tavern_table_b'], pose: 'seated', prop: 'dice', score: 'cohesion', need: 'social', who: 'adult' },
  { state: 'storytelling', stations: ['story_circle'], pose: 'standing', prop: '', score: 'cohesion', need: 'social', who: 'elder' },
  { state: 'listening_seated', stations: ['story_circle'], pose: 'seated_floor', prop: '', score: 'learning', need: 'social', who: 'any' },
  { state: 'dancing', stations: ['dance_floor'], pose: 'exertion', prop: '', score: 'cohesion', need: 'social', who: 'any' },
  { state: 'exercising', stations: ['green', 'bath_yard'], pose: 'exertion', prop: '', score: 'health', need: 'none', who: 'any' },
  { state: 'drilling', stations: ['green'], pose: 'exertion', prop: '', score: 'readiness', need: 'none', who: 'adult' },
  { state: 'wrestling', stations: ['wrestling_ring'], pose: 'exertion', prop: '', score: 'readiness', need: 'social', who: 'adult' },
  { state: 'shooting', stations: ['butts'], pose: 'standing', prop: '', score: 'readiness', need: 'none', who: 'adult' },
  { state: 'queueing', stations: ['ration_line'], pose: 'standing', prop: '', score: 'order', need: 'none', who: 'any' },
  { state: 'browsing', stations: ['market_browse'], pose: 'standing', prop: '', score: 'prosperity', need: 'none', who: 'adult' },
  { state: 'praying_kneel', stations: ['kneelers'], pose: 'kneeling', prop: 'candle', score: 'piety', need: 'rest', who: 'any' },
  { state: 'learning', stations: ['school_mat'], pose: 'seated_floor', prop: '', score: 'learning', need: 'none', who: 'child' },
  { state: 'teaching', stations: ['school_mat'], pose: 'standing', prop: '', score: 'learning', need: 'none', who: 'adult' },
  { state: 'fetching_water', stations: ['well_queue'], pose: 'standing', prop: 'bucket', score: 'health', need: 'none', who: 'any' },
  { state: 'tending', stations: ['infirmary_cots'], pose: 'stoop', prop: '', score: 'health', need: 'none', who: 'adult' },
  { state: 'convalescing', stations: ['infirmary_cots'], pose: 'lying', prop: '', score: '', need: 'rest', who: 'any' },
  { state: 'studying', stations: ['archive_desks'], pose: 'seated', prop: 'ledger', score: 'learning', need: 'none', who: 'adult' },
  // at home or on a route — no station
  { state: 'gardening', stations: [], pose: 'stoop', prop: 'basket', score: 'prosperity', need: 'rest', who: 'any' },
  { state: 'chopping', stations: [], pose: 'exertion', prop: 'axe', score: '', need: 'none', who: 'adult' },
  { state: 'sweeping', stations: [], pose: 'walk', prop: 'broom', score: 'health', need: 'none', who: 'adult' },
  { state: 'lamplighting', stations: [], pose: 'walk', prop: 'pole', score: 'order', need: 'none', who: 'adult' },
  { state: 'crying_news', stations: [], pose: 'standing', prop: 'bell', score: 'order', need: 'none', who: 'adult' },
  { state: 'carting', stations: [], pose: 'walk', prop: 'crate', score: 'prosperity', need: 'none', who: 'adult' },
  { state: 'strolling', stations: [], pose: 'walk', prop: '', score: 'cohesion', need: 'social', who: 'adult' },
  { state: 'skywatching', stations: [], pose: 'standing', prop: '', score: '', need: 'rest', who: 'any' },
  { state: 'performing', stations: ['dance_floor'], pose: 'exertion', prop: '', score: 'cohesion', need: 'none', who: 'adult' },
  // 2026-09-24: an evening round the fire (the fire circle's log benches).
  { state: 'fireside', stations: ['fire_circle'], pose: 'seated', prop: '', score: 'cohesion', need: 'social', who: 'any' },
];

const ACTIVITY_BY_STATE: Record<string, ActivityDef> = Object.fromEntries(ACTIVITIES.map((a) => [a.state, a]));
export function activityDef(state: string): ActivityDef | null {
  return ACTIVITY_BY_STATE[state] ?? null;
}
/** Every activity state the wave adds (harness coverage of the clip grammar). */
export const ACTIVITY_STATES: string[] = ACTIVITIES.map((a) => a.state);

/** Radius inside which a versona in a hosted state counts as AT a station. */
export const STATION_REACH_M = 5;

/**
 * Where formation slot `i` of `n` sits, relative to the station (x, z in the
 * station's own frame, +z = the way it faces) and which way the occupant faces
 * (radians, 0 = the station's facing). Pure: the client calls it for every
 * occupant it seats, so all clients draw the same picture.
 */
export function formationSlot(f: Formation, i: number, n: number, spacing: number): { x: number; z: number; face: number } {
  const count = Math.max(1, n);
  switch (f) {
    case 'row': return { x: (i - (count - 1) / 2) * spacing, z: 0, face: 0 };
    case 'queue': return { x: 0, z: -i * spacing, face: 0 };
    case 'table': {
      // two a side, facing each other across the table
      const side = i % 2 === 0 ? -1 : 1;
      const along = (Math.floor(i / 2) - (Math.ceil(count / 2) - 1) / 2) * spacing;
      return { x: along, z: side * spacing * 0.75, face: side < 0 ? 0 : Math.PI };
    }
    case 'circle': case 'ring': {
      const r = Math.max(spacing, (spacing * count) / (2 * Math.PI));
      const a = (i / count) * Math.PI * 2;
      // a circle faces its centre; a ring of onlookers does too
      return { x: Math.sin(a) * r, z: Math.cos(a) * r, face: a + Math.PI };
    }
    case 'arc': {
      // a half-circle of listeners in front of the focus at the station origin
      const r = Math.max(2.2, spacing * count / Math.PI);
      const a = count === 1 ? 0 : -Math.PI / 2.6 + (i / (count - 1)) * (Math.PI / 1.3);
      return { x: Math.sin(a) * r, z: Math.cos(a) * r, face: a + Math.PI };
    }
    case 'grid': {
      const cols = Math.ceil(Math.sqrt(count));
      const row = Math.floor(i / cols), col = i % cols;
      return { x: (col - (cols - 1) / 2) * spacing, z: -row * spacing, face: 0 };
    }
  }
}
